import axios from 'axios';
import Cookies from 'js-cookie';
import { notifyGlobal } from '../utils/globalNotifier';
import { clearDiscountRules } from '../utils/discountRules';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

const api = axios.create({
  baseURL: API_BASE,
  timeout: 180000,
  headers: { 'Content-Type': 'application/json' },
});

// Attach JWT token from cookie to every request
api.interceptors.request.use((config) => {
  const token = Cookies.get('authToken');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 — redirect to login.
// A 403 with errorCode FEATURE_ACCESS_RESTRICTED means the user is authenticated but lacks
// read/write access to this feature (per-user access control) — not an auth failure, so it
// must NOT log the user out. Every other 403 (e.g. non-admin hitting an admin-only route)
// keeps the existing logout behavior.
// Every 403 — whichever kind — is surfaced as a toast here, once, centrally, so no
// individual page/component needs its own handling for "access denied" responses.
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const errorCode = error.response?.data?.errorCode;

    if (status === 403) {
      notifyGlobal(error.response?.data?.message || 'You do not have access to this action.', 'error');
    }

    if (status === 401 || (status === 403 && errorCode !== 'FEATURE_ACCESS_RESTRICTED')) {
      Cookies.remove('authToken');
      Cookies.remove('userInfo');
      clearDiscountRules();
      window.location.href = '/products/order-tracking/login';
    }
    return Promise.reject(error);
  }
);

// True when the request was blocked by per-user feature access (e.g. Barcode Scan switched off).
// The interceptor above already showed the toast, so callers only need to reset their own UI.
export function isFeatureAccessRestricted(error) {
  return error?.response?.data?.errorCode === 'FEATURE_ACCESS_RESTRICTED';
}

export default api;
